<!-- views/Home.vue -->
<template>
  <div class="container page-global">
    <section class="hero" aria-labelledby="brand-heading">
      <div class="hero-content">
        <h1 id="brand-heading">Cashless FYI</h1>
        <p class="tagline">
          Free ROI calculators for cashless programs in healthcare, senior living, and corporate dining
        </p>
        <p class="explainer">
          Model cost savings, revenue growth, and payback with vendor-neutral tools—so you can decide
          whether going cashless makes sense before you talk to any provider.
        </p>
        <div class="hero-actions">
          <a href="#calculators" class="cta-button">
            <span>Choose a calculator</span>
            <span class="cta-arrow" aria-hidden="true">→</span>
          </a>
          <router-link to="/calculators" class="ghost-cta">View all tools</router-link>
        </div>
      </div>
    </section>

    <section id="calculators" class="calculator-actions" aria-labelledby="calculators-heading">
      <h2 id="calculators-heading">Start with the right calculator</h2>
      <p class="section-lead">
        Pick the tool that matches your market. Each one walks through your numbers and returns
        projections you can share with leadership.
      </p>
      <div class="calc-grid">
        <article class="calc-card">
          <h3>Payroll Deduction ROI</h3>
          <p class="calc-market">Healthcare &amp; senior living</p>
          <p>
            Estimate ticket lift, participation, traffic impact, payback period, and annual profit
            when employees and staff pay through payroll deduction.
          </p>
          <router-link to="/calculators/payroll-deduction" class="calc-cta">
            Open payroll deduction calculator
          </router-link>
        </article>
        <article class="calc-card">
          <h3>Corporate Dining ROI</h3>
          <p class="calc-market">Workplace dining</p>
          <p>
            Evaluate participation, retention, menu and data optimization, and how cashless stacks
            up against off-site dining options.
          </p>
          <router-link to="/calculators/corporate-dining" class="calc-cta">
            Open corporate dining calculator
          </router-link>
        </article>
        <article class="calc-card">
          <h3>Participation ROI</h3>
          <p class="calc-market">Operations leverage</p>
          <p>
            Compare participation rate, transaction boost, and labor refinement against Prime Cost
            and profitability over 1, 3, and 5 years.
          </p>
          <router-link to="/calculators/participation" class="calc-cta">
            Open participation calculator
          </router-link>
        </article>
      </div>
    </section>

    <section class="audiences" aria-labelledby="audiences-heading">
      <h2 id="audiences-heading">Built for operators making the call</h2>
      <p class="section-lead">
        Whether you run cafés on a care campus or dining for a corporate HQ, the questions are the same:
        what does cashless change, and how long until it pays back?
      </p>
      <ul class="audience-list">
        <li>
          <strong>Healthcare &amp; senior living</strong>
          — Staff dining, resident/employee commerce, and payroll-linked payments.
        </li>
        <li>
          <strong>Corporate dining</strong>
          — Participation, retention, and competing with nearby restaurants and delivery.
        </li>
        <li>
          <strong>Finance &amp; ops leaders</strong>
          — Clear assumptions, payback math, and shareable projections—not a sales deck.
        </li>
      </ul>
    </section>

    <section class="how-it-works" aria-labelledby="how-heading">
      <h2 id="how-heading">How it works</h2>
      <div class="steps">
        <div class="step">
          <div class="step-number" aria-hidden="true">1</div>
          <h3>Choose your calculator</h3>
          <p>Select the ROI tool that matches your industry and use case.</p>
        </div>
        <div class="step">
          <div class="step-number" aria-hidden="true">2</div>
          <h3>Enter your data</h3>
          <p>Provide straightforward inputs about sales, transactions, and assumptions.</p>
        </div>
        <div class="step">
          <div class="step-number" aria-hidden="true">3</div>
          <h3>Get your results</h3>
          <p>Review projections, charts, and payback insights tailored to what you entered.</p>
        </div>
      </div>
    </section>

    <section class="trust" aria-labelledby="trust-heading">
      <h2 id="trust-heading">Vendor-neutral by design</h2>
      <p class="section-lead">
        Cashless FYI is an educational front door—not a product demo. The site is provided by
        <a href="https://www.newcare.io" target="_blank" rel="noopener noreferrer">Newcare</a>,
        but we keep learning separate from buying so you can explore trade-offs without pressure.
      </p>
    </section>

    <section class="faq" aria-labelledby="faq-heading">
      <h2 id="faq-heading">Frequently asked questions</h2>
      <div class="faq-list">
        <details v-for="item in faqs" :key="item.q" class="faq-item">
          <summary>{{ item.q }}</summary>
          <p>{{ item.a }}</p>
        </details>
      </div>
    </section>

    <div class="footer-teaser">
      <div class="nocash-logo">
        <img src="../assets/logo.png" alt="" class="small-logo" width="40" height="40">
        <span>No Cash Zone</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      faqs: [
        {
          q: 'What is Cashless FYI?',
          a: 'Cashless FYI is a free, vendor-neutral educational site that helps healthcare, senior living, and corporate dining operators understand the financial impact of cashless programs—before talking to vendors.',
        },
        {
          q: 'Are the cashless ROI calculators free?',
          a: 'Yes. All calculators are free to use. You can model payroll deduction, corporate dining, and participation ROI without creating an account or speaking with sales.',
        },
        {
          q: 'Who should use these calculators?',
          a: 'Operators, finance leaders, foodservice directors, and anyone evaluating cashless programs who wants numbers and assumptions before a vendor conversation.',
        },
        {
          q: 'Does Cashless FYI sell cashless software?',
          a: 'No. This site is educational. It is provided by Newcare International, LLC, but education (Cashless FYI) is intentionally separated from Newcare’s product so you can explore ROI without a pitch.',
        },
        {
          q: 'What data do I need to run a calculator?',
          a: 'Basic operational inputs such as sales or transaction volume, average ticket, participation assumptions, and related costs. Each tool walks you through the fields.',
        },
        {
          q: 'Which calculator should I start with?',
          a: 'Use Payroll Deduction ROI for healthcare and senior living staff programs. Use Corporate Dining ROI for workplace dining. Use Participation ROI when you want to compare participation, ticket, and labor levers over multiple years.',
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/app.scss';

.hero {
  padding: 4.5rem 0 3.5rem;
  text-align: center;

  .hero-content {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 1.5rem;
  }

  h1 {
    font-size: 3.5rem;
    font-weight: 900;
    margin-bottom: 1.25rem;
    color: $body-color;
    line-height: 1.15;
    letter-spacing: -0.02em;
  }

  .tagline {
    font-size: 1.45rem;
    color: $secondary;
    max-width: 720px;
    margin: 0 auto 1.5rem auto;
    font-weight: 500;
    line-height: 1.4;
  }

  .explainer {
    font-size: 1.1rem;
    color: rgba($body-color, 0.9);
    max-width: 760px;
    margin: 0 auto 2.25rem auto;
    line-height: 1.7;
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    align-items: center;
  }

  .cta-button {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1.15rem 2.25rem;
    background: $primary;
    color: $body-bg;
    border-radius: 8px;
    font-size: 1.2rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba($primary, 0.3);

    &:hover {
      background: lighten($primary, 10%);
      color: $body-bg;
      text-decoration: none;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba($primary, 0.4);
    }

    .cta-arrow {
      font-size: 1.4rem;
      transition: transform 0.3s ease;
    }

    &:hover .cta-arrow {
      transform: translateX(4px);
    }
  }

  .ghost-cta {
    color: $primary;
    font-weight: 600;
    text-decoration: none;
    padding: 0.75rem 1rem;

    &:hover {
      color: lighten($primary, 10%);
      text-decoration: underline;
    }
  }
}

.section-lead {
  font-size: 1.1rem;
  color: rgba($body-color, 0.9);
  max-width: 760px;
  margin: 0 auto 2.25rem;
  line-height: 1.65;
  text-align: center;

  a {
    color: $primary;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.calculator-actions,
.audiences,
.how-it-works,
.trust,
.faq {
  padding: 4rem 0;
  border-top: 1px solid rgba($body-color, 0.1);

  h2 {
    font-size: 2.25rem;
    font-weight: 800;
    color: $body-color;
    margin-bottom: 1rem;
    text-align: center;
  }
}

.calc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: left;
}

.calc-card {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.12);
  border-radius: 12px;
  padding: 1.75rem 1.5rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  h3 {
    font-size: 1.35rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 0.35rem;
  }

  .calc-market {
    font-size: 0.9rem;
    color: $secondary;
    font-weight: 600;
    margin-bottom: 0.85rem;
  }

  p {
    font-size: 1rem;
    color: rgba($body-color, 0.88);
    line-height: 1.55;
    margin-bottom: 1.25rem;
    flex-grow: 1;
  }

  .calc-cta {
    align-self: flex-start;
    padding: 0.7rem 1.25rem;
    background: $primary;
    color: $body-bg;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.2s ease, transform 0.15s ease;

    &:hover {
      background: lighten($primary, 10%);
      color: $body-bg;
      text-decoration: none;
      transform: translateY(-1px);
    }
  }
}

.audience-list {
  list-style: none;
  padding: 0 1.5rem;
  margin: 0 auto;
  max-width: 720px;
  text-align: left;

  li {
    padding: 1rem 0;
    border-bottom: 1px solid rgba($body-color, 0.08);
    font-size: 1.05rem;
    color: rgba($body-color, 0.9);
    line-height: 1.55;

    &:last-child {
      border-bottom: none;
    }

    strong {
      color: $body-color;
    }
  }
}

.how-it-works {
  text-align: center;

  .steps {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 2.5rem;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 1.5rem;
  }

  .step {
    .step-number {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: $primary;
      color: $body-bg;
      font-size: 1.6rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.25rem;
      box-shadow: 0 4px 12px rgba($primary, 0.3);
    }

    h3 {
      font-size: 1.3rem;
      font-weight: 700;
      color: $body-color;
      margin-bottom: 0.75rem;
    }

    p {
      font-size: 1.05rem;
      color: rgba($body-color, 0.85);
      line-height: 1.6;
      margin: 0;
    }
  }
}

.faq-list {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 1.25rem;
  text-align: left;
}

.faq-item {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  padding: 0.35rem 1rem;

  summary {
    cursor: pointer;
    font-weight: 650;
    font-size: 1.05rem;
    color: $body-color;
    padding: 0.85rem 0;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }

    &::before {
      content: '+';
      display: inline-block;
      width: 1.25rem;
      color: $primary;
      font-weight: 700;
      margin-right: 0.35rem;
    }
  }

  &[open] summary::before {
    content: '–';
  }

  p {
    margin: 0 0 1rem 1.6rem;
    color: rgba($body-color, 0.88);
    line-height: 1.65;
    font-size: 1rem;
  }
}

.footer-teaser {
  margin-top: 2rem;
  margin-bottom: 1rem;
  text-align: center;

  .nocash-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: $body-color;
  }

  .small-logo {
    width: 40px;
    height: auto;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 3rem 0 2rem;

    h1 {
      font-size: 2.35rem;
    }

    .tagline {
      font-size: 1.15rem;
    }

    .explainer {
      font-size: 1rem;
    }

    .cta-button {
      font-size: 1.05rem;
      padding: 1rem 1.75rem;
    }
  }

  .calculator-actions,
  .audiences,
  .how-it-works,
  .trust,
  .faq {
    padding: 3rem 0;

    h2 {
      font-size: 1.85rem;
    }
  }

  .how-it-works .steps {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}
</style>
